// AnalyticsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Chart as ChartJS,
    CategoryScale, LinearScale,
    PointElement, LineElement, BarElement, ArcElement,
    Title, Tooltip, Legend
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import Select from "react-select";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./AnalyticsPage.css";

ChartJS.register(
    CategoryScale, LinearScale,
    PointElement, LineElement, BarElement, ArcElement,
    Title, Tooltip, Legend
);

const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const defaultLodgeDocumentTypeOptions = [
    { value: "001", label: "Agenda" },
    { value: "002", label: "Minutes" },
];
const defaultCoverageLodgeCodes = [
    "COM", "SANO",
    "247", "265", "288", "361", "364", "370", "392", "400", "523", "743", "767", "773", "853", "862", "901",
    "DGRACSANIC", "338_RA_IC", "364_RA_IC", "523_RA_IC", "782_RA_IC",
    "SHAM26", "388_KT",
];
const coverageGranularityOptions = [
    { value: "auto", label: "Auto" },
    { value: "month", label: "Month" },
    { value: "quarter", label: "Quarter" },
    { value: "year", label: "Year" },
];

const countryCentroids = {
    ZA: { lat: -30.5595, lng: 22.9375, label: "South Africa" },
    US: { lat: 39.8283, lng: -98.5795, label: "United States" },
    GB: { lat: 55.3781, lng: -3.436, label: "United Kingdom" },
    IE: { lat: 53.1424, lng: -7.6921, label: "Ireland" },
    AU: { lat: -25.2744, lng: 133.7751, label: "Australia" },
    NZ: { lat: -40.9006, lng: 174.886, label: "New Zealand" },
    CA: { lat: 56.1304, lng: -106.3468, label: "Canada" },
    DE: { lat: 51.1657, lng: 10.4515, label: "Germany" },
    FR: { lat: 46.2276, lng: 2.2137, label: "France" },
    NL: { lat: 52.1326, lng: 5.2913, label: "Netherlands" },
};

const cityCentroids = {
    "ZA:johannesburg": { lat: -26.2041, lng: 28.0473 },
    "ZA:pretoria": { lat: -25.7479, lng: 28.2293 },
    "ZA:tshwane": { lat: -25.7479, lng: 28.2293 },
    "ZA:centurion": { lat: -25.8601, lng: 28.1894 },
    "ZA:benoni": { lat: -26.1885, lng: 28.3208 },
    "ZA:kempton park": { lat: -26.0963, lng: 28.2336 },
    "ZA:boksburg": { lat: -26.2326, lng: 28.2400 },
    "ZA:germiston": { lat: -26.2259, lng: 28.1677 },
    "ZA:durban": { lat: -29.8587, lng: 31.0218 },
    "ZA:cape town": { lat: -33.9249, lng: 18.4241 },
    "ZA:bloemfontein": { lat: -29.0852, lng: 26.1596 },
    "ZA:port elizabeth": { lat: -33.9608, lng: 25.6022 },
    "ZA:gqeberha": { lat: -33.9608, lng: 25.6022 },
};

const isUsableCoordinate = (latitude, longitude) => (
    Number.isFinite(latitude)
    && Number.isFinite(longitude)
    && Math.abs(latitude) <= 90
    && Math.abs(longitude) <= 180
    && !(latitude === 0 && longitude === 0)
);

function FitMapToPoints({ points }) {
    const map = useMap();

    useEffect(() => {
        const validPoints = points
            .map((row) => row.point)
            .filter((point) => point && isUsableCoordinate(point.lat, point.lng));

        if (!validPoints.length) return;

        if (validPoints.length === 1) {
            map.setView([validPoints[0].lat, validPoints[0].lng], validPoints[0].precision === "country" ? 4 : 7);
            return;
        }

        const bounds = validPoints.map((point) => [point.lat, point.lng]);
        map.fitBounds(bounds, { padding: [36, 36], maxZoom: 7 });
    }, [map, points]);

    return null;
}

function VisitorHeatLayer({ points }) {
    const map = useMap();
    const [mapVersion, setMapVersion] = useState(0);

    useEffect(() => {
        const refresh = () => setMapVersion((value) => value + 1);
        map.on("move zoom resize", refresh);
        refresh();
        return () => {
            map.off("move zoom resize", refresh);
        };
    }, [map]);

    const maxVisitors = Math.max(1, ...points.map((row) => Number(row.visitors || row.sessions || 0)));
    const zoom = map.getZoom();
    const clusterDistance = Math.max(42, 96 - zoom * 10);
    const heatPoints = points.map((row) => {
        const visitors = Number(row.visitors || row.sessions || 0);
        const position = map.latLngToContainerPoint([row.point.lat, row.point.lng]);
        return { ...row, visitors, position };
    });
    const badgeClusters = heatPoints.reduce((clusters, row) => {
        const nearbyCluster = clusters.find((cluster) => {
            const xDistance = cluster.x - row.position.x;
            const yDistance = cluster.y - row.position.y;
            return Math.sqrt(xDistance * xDistance + yDistance * yDistance) <= clusterDistance;
        });

        if (nearbyCluster) {
            const nextVisitors = nearbyCluster.visitors + row.visitors;
            nearbyCluster.x = ((nearbyCluster.x * nearbyCluster.visitors) + (row.position.x * row.visitors)) / nextVisitors;
            nearbyCluster.y = ((nearbyCluster.y * nearbyCluster.visitors) + (row.position.y * row.visitors)) / nextVisitors;
            nearbyCluster.visitors = nextVisitors;
            nearbyCluster.locations += 1;
            nearbyCluster.labels.push(row.label);
            nearbyCluster.points.push(row);
            return clusters;
        }

        clusters.push({
            x: row.position.x,
            y: row.position.y,
            visitors: row.visitors,
            locations: 1,
            labels: [row.label],
            points: [row],
        });
        return clusters;
    }, []);
    const zoomToCluster = (cluster) => {
        if (cluster.points.length <= 1) {
            const point = cluster.points[0].point;
            map.setView([point.lat, point.lng], Math.min(map.getZoom() + 2, 13), { animate: true });
            return;
        }

        map.fitBounds(
            cluster.points.map((row) => [row.point.lat, row.point.lng]),
            { animate: true, padding: [44, 44], maxZoom: 12 }
        );
    };
    void mapVersion;

    return (
        <div className="analytics-visitor-heat-layer">
            {heatPoints.map((row) => {
                const intensity = Math.max(0.18, row.visitors / maxVisitors);
                const heatSize = Math.max(92, Math.min(250, 76 + Math.sqrt(row.visitors) * 10 + zoom * 5));
                return (
                    <span
                        className="analytics-visitor-heat"
                        key={`${row.country}-${row.region}-${row.city}-${row.point.lat}-${row.point.lng}`}
                        style={{
                            left: `${row.position.x}px`,
                            top: `${row.position.y}px`,
                            width: `${heatSize}px`,
                            height: `${heatSize}px`,
                            opacity: Math.max(0.42, Math.min(0.96, 0.42 + intensity * 0.54)),
                        }}
                    />
                );
            })}
            {badgeClusters.map((cluster, index) => {
                const badgeSize = Math.max(34, Math.min(62, 32 + Math.sqrt(cluster.visitors) * 2.1));
                return (
                    <button
                        type="button"
                        className={`analytics-visitor-count${cluster.locations > 1 ? " clustered" : ""}`}
                        key={`${cluster.x}-${cluster.y}-${index}`}
                        onClick={() => zoomToCluster(cluster)}
                        style={{
                            left: `${cluster.x}px`,
                            top: `${cluster.y}px`,
                            width: `${badgeSize}px`,
                            height: `${badgeSize}px`,
                        }}
                        title={`${cluster.labels.slice(0, 4).join(", ")}${cluster.labels.length > 4 ? "..." : ""}: ${cluster.visitors.toLocaleString()} mapped visitor${cluster.visitors === 1 ? "" : "s"}`}
                    >
                        {cluster.visitors.toLocaleString()}
                    </button>
                );
            })}
        </div>
    );
}

const formatCount = (value) => Number(value || 0).toLocaleString();
const formatPercent = (value) => `${Math.round(Number(value || 0))}%`;

const countryName = (countryCode) => {
    const code = String(countryCode || "").trim().toUpperCase();
    if (!code || code === "UNKNOWN") return "Unknown";
    if (countryCentroids[code]?.label) return countryCentroids[code].label;
    try {
        return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
    } catch {
        return code;
    }
};

const locationPoint = (row, country) => {
    const latitude = Number(row.latitude);
    const longitude = Number(row.longitude);
    if (isUsableCoordinate(latitude, longitude)) {
        return { lat: latitude, lng: longitude, precision: row.precision || (row.city ? "city" : "region") };
    }
    const cityKey = `${country}:${String(row.city || "").trim().toLowerCase()}`;
    if (cityCentroids[cityKey]) return { ...cityCentroids[cityKey], precision: "city" };
    if (countryCentroids[country]) return { ...countryCentroids[country], precision: "country" };
    return null;
};

const normalizeDocTypeLabel = (value) => {
    if (!value) return "Unknown";
    return String(value)
        .replace(/_/g, " ")
        .replace(/\b\w/g, char => char.toUpperCase());
};

const monthKeyToLabel = (monthKey) => {
    try {
        const d = new Date(`${monthKey}-01T00:00:00Z`);
        return d.toLocaleDateString("en-ZA", {
            timeZone: "Africa/Johannesburg",
            month: "short",
            year: "2-digit",
        });
    } catch {
        return monthKey;
    }
};

const periodKeyToLabel = (periodKey, granularity) => {
    if (granularity === "year") return periodKey;
    if (granularity === "quarter") {
        const [year, quarter] = periodKey.split("-Q");
        return `Q${quarter} ${year.slice(2)}`;
    }
    return monthKeyToLabel(periodKey);
};

const getPeriodKey = (monthKey, granularity) => {
    if (granularity === "year") return monthKey.slice(0, 4);
    if (granularity === "quarter") {
        const monthIndex = Number(monthKey.slice(5, 7)) - 1;
        return `${monthKey.slice(0, 4)}-Q${Math.floor(monthIndex / 3) + 1}`;
    }
    return monthKey;
};

const resolveCoverageGranularity = (monthCount, selectedGranularity) => {
    if (selectedGranularity !== "auto") return selectedGranularity;
    if (monthCount <= 24) return "month";
    if (monthCount <= 60) return "quarter";
    return "year";
};

const getMonthKeysBetween = (fromDate, toDate) => {
    if (!fromDate || !toDate) return [];
    const start = new Date(`${fromDate.slice(0, 7)}-01T00:00:00Z`);
    const end = new Date(`${toDate.slice(0, 7)}-01T00:00:00Z`);
    const months = [];

    for (
        const cursor = new Date(start);
        cursor <= end;
        cursor.setUTCMonth(cursor.getUTCMonth() + 1)
    ) {
        months.push(cursor.toISOString().slice(0, 7));
    }

    return months;
};

const toSASTDateLabel = (isoOrDateStr) => {
    try {
        const d = new Date(
            /^\d{4}-\d{2}-\d{2}$/.test(isoOrDateStr)
                ? `${isoOrDateStr}T00:00:00Z`
                : isoOrDateStr
        );
        return d.toLocaleDateString("en-ZA", {
            timeZone: "Africa/Johannesburg",
            day: "2-digit",
            month: "short",
        });
    } catch {
        return isoOrDateStr;
    }
};

const toSASTDateTime = (isoStr) => {
    try {
        const d = new Date(isoStr);
        return d.toLocaleString("en-ZA", {
            timeZone: "Africa/Johannesburg",
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    } catch {
        return isoStr;
    }
};

export default function AnalyticsPage() {
    // Filters
    const [from, setFrom] = useState(() =>
        new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)
    ); // last 30d
    const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
    const [path, setPath] = useState("");
    const [userId, setUserId] = useState("");
    const [activeLens, setActiveLens] = useState("overview");

    // Data
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [recent, setRecent] = useState([]);
    const [extra, setExtra] = useState(null);
    const [researchContext, setResearchContext] = useState(null);

    // NEW: Downloads analytics
    const [dlByUser, setDlByUser] = useState([]); // [{ user_id, first_name, last_name, display, downloads }]
    const [dlByDoc, setDlByDoc] = useState([]);   // [{ document_id, title, downloads }]

    // Archive analysis
    const [archiveInsights, setArchiveInsights] = useState(null);
    const [archiveInsightsLoading, setArchiveInsightsLoading] = useState(false);
    const [archiveInsightsError, setArchiveInsightsError] = useState("");
    const [expectedMeetingGaps, setExpectedMeetingGaps] = useState(null);
    const [expectedMeetingGapsLoading, setExpectedMeetingGapsLoading] = useState(false);
    const [expectedMeetingGapsError, setExpectedMeetingGapsError] = useState("");
    const [selectedExpectedGapCell, setSelectedExpectedGapCell] = useState(null);
    const [expectedDocumentTypes, setExpectedDocumentTypes] = useState(defaultLodgeDocumentTypeOptions);
    const [expectedDocumentTypeOptions, setExpectedDocumentTypeOptions] = useState(defaultLodgeDocumentTypeOptions);
    const [expectedEventTypes, setExpectedEventTypes] = useState([]);
    const [expectedEventTypeOptions, setExpectedEventTypeOptions] = useState([]);
    const [coverageLodgeCodes, setCoverageLodgeCodes] = useState(defaultCoverageLodgeCodes);
    const [coverageLodgeOptions, setCoverageLodgeOptions] = useState([]);
    const [selectedCoverageLodge, setSelectedCoverageLodge] = useState(null);
    const [coverageLodgeSearch, setCoverageLodgeSearch] = useState("");
    const [showCoverageLodgePicker, setShowCoverageLodgePicker] = useState(false);
    const [coverageLodgeConfigSaving, setCoverageLodgeConfigSaving] = useState(false);
    const [coverageLodgeConfigMessage, setCoverageLodgeConfigMessage] = useState("");

    // Lodge document summary
    const [showLodgeSummary, setShowLodgeSummary] = useState(false);
    const [lodgeSummaryLoading, setLodgeSummaryLoading] = useState(false);
    const [lodgeSummaryError, setLodgeSummaryError] = useState("");
    const [lodgeSummary, setLodgeSummary] = useState(null);
    const lodgeDocumentTypes = defaultLodgeDocumentTypeOptions;
    const [lodgeSummaryView, setLodgeSummaryView] = useState("coverage");
    const [coverageGranularity, setCoverageGranularity] = useState("auto");
    const [selectedCoverageCell, setSelectedCoverageCell] = useState(null);

    // Dropdowns
    const [pathsDropdown, setPathsDropdown] = useState([]);
    const [usersDropdown, setUsersDropdown] = useState([]);

    // Prime dropdowns once
    useEffect(() => {
        (async () => {
            try {
                const [pathsRes, uploadersRes, documentTypesRes, eventTypesRes] = await Promise.all([
                    fetch(`/api/v1/analytics/overview?from=${from}&to=${to}`),
                    fetch(`/api/uploaders`), // [{ user_id, display }]
                    fetch(`/api/document-types`),
                    fetch(`/api/v1/eventtypes`)
                ]);
                const ov = await pathsRes.json();
                setPathsDropdown((ov.topPaths || []).map(p => ({ value: p.path, label: p.path })));

                const ups = await uploadersRes.json();
                setUsersDropdown(ups.map(u => ({ value: String(u.user_id), label: u.display })));

                const docTypes = await documentTypesRes.json();
                const docTypeOptions = (Array.isArray(docTypes) ? docTypes : [])
                    .map(docType => ({
                        value: docType.code,
                        label: docType.description,
                    }))
                    .filter(option => option.value && option.label)
                    .sort((a, b) => a.label.localeCompare(b.label));
                if (docTypeOptions.length) {
                    setExpectedDocumentTypeOptions(docTypeOptions);
                    setExpectedDocumentTypes(defaultLodgeDocumentTypeOptions.map(defaultOption =>
                        docTypeOptions.find(option => option.value === defaultOption.value) || defaultOption
                    ));
                }

                const eventTypes = await eventTypesRes.json();
                const eventTypeOptions = (Array.isArray(eventTypes) ? eventTypes : [])
                    .map(eventType => ({
                        value: eventType.code,
                        label: eventType.description,
                    }))
                    .filter(option => option.value && option.label)
                    .sort((a, b) => a.label.localeCompare(b.label));
                setExpectedEventTypeOptions(eventTypeOptions);
            } catch {
                /* non-critical */
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        const qs = new URLSearchParams({ from, to });
        if (path) qs.set("path", path);
        if (userId) qs.set("userId", userId);

        try {
            const [ovRes, rcRes, exRes, dluRes, dldRes, researchRes] = await Promise.all([
                fetch(`/api/v1/analytics/overview?${qs.toString()}`),
                fetch(`/api/v1/analytics/recent?limit=20`),
                fetch(`/api/v1/analytics/users-extra?from=${from}&to=${to}&limit=10`),

                // NEW: downloads grouped endpoints
                fetch(`/api/v1/analytics/downloads-by-user?${qs.toString()}&limit=10`),
                fetch(`/api/v1/analytics/downloads-by-document?${qs.toString()}&limit=10`),
                fetch(`/api/public/site-stats`),
            ]);

            const [ov, rc, ex, dlu, dld, research] = await Promise.all([
                ovRes.json(),
                rcRes.json(),
                exRes.json(),
                dluRes.json(),
                dldRes.json(),
                researchRes.json(),
            ]);

            setOverview(ov);
            setRecent(rc.items || []);
            setExtra(ex);
            setResearchContext(research);
            setDlByUser(Array.isArray(dlu?.items) ? dlu.items : []);
            setDlByDoc(Array.isArray(dld?.items) ? dld.items : []);
        } catch (e) {
            console.error("Analytics load error:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, path, userId]);

    const loadArchiveInsights = async () => {
        setArchiveInsightsLoading(true);
        setArchiveInsightsError("");

        const qs = new URLSearchParams({ from, to });
        const selectedTypes = lodgeDocumentTypes.flatMap(type => type.value.split(","));
        if (selectedTypes.length) qs.set("documentTypes", selectedTypes.join(","));

        try {
            const res = await fetch(`/api/v1/analytics/archive-insights?${qs.toString()}`);
            if (!res.ok) {
                throw new Error(`Request failed with ${res.status}`);
            }
            const data = await res.json();
            setArchiveInsights(data);
        } catch (e) {
            console.error("Archive insights load error:", e);
            setArchiveInsights(null);
            setArchiveInsightsError("Archive insights endpoint is not available yet.");
        } finally {
            setArchiveInsightsLoading(false);
        }
    };

    const loadExpectedMeetingGaps = async () => {
        setExpectedMeetingGapsLoading(true);
        setExpectedMeetingGapsError("");

        const qs = new URLSearchParams({ from, to });
        const selectedDocumentTypes = expectedDocumentTypes.map(type => type.value).filter(Boolean);
        const selectedEventTypes = expectedEventTypes.map(type => type.value).filter(Boolean);
        if (selectedDocumentTypes.length) qs.set("documentTypes", selectedDocumentTypes.join(","));
        if (selectedEventTypes.length) qs.set("eventTypes", selectedEventTypes.join(","));
        const activeLodgeCodes = selectedCoverageLodge?.value
            ? [selectedCoverageLodge.value]
            : coverageLodgeCodes;
        if (activeLodgeCodes.length) qs.set("lodgeCodes", activeLodgeCodes.join(","));
        try {
            const res = await fetch(`/api/v1/analytics/expected-meeting-gaps?${qs.toString()}`);
            if (!res.ok) {
                let message = `Request failed with ${res.status}`;
                try {
                    const errorData = await res.json();
                    message = errorData?.error || message;
                } catch (_error) {
                    // Keep the status fallback if the server did not return JSON.
                }
                throw new Error(message);
            }
            const data = await res.json();
            setExpectedMeetingGaps(data);
        } catch (e) {
            console.error("Expected meeting gaps load error:", e);
            setExpectedMeetingGaps(null);
            setExpectedMeetingGapsError(e.message || "Expected agenda and minutes audit is not available yet.");
        } finally {
            setExpectedMeetingGapsLoading(false);
        }
    };

    const loadCoverageLodgeConfig = async () => {
        try {
            const res = await fetch("/api/v1/analytics/coverage-lodge-config");
            if (!res.ok) throw new Error(`Request failed with ${res.status}`);
            const data = await res.json();
            const lodges = Array.isArray(data.lodges) ? data.lodges : [];
            const selectedCodes = Array.isArray(data.selectedCodes) && data.selectedCodes.length
                ? data.selectedCodes
                : defaultCoverageLodgeCodes;
            setCoverageLodgeOptions(lodges);
            setCoverageLodgeCodes(selectedCodes);
        } catch (e) {
            console.error("Coverage lodge config load error:", e);
            setCoverageLodgeOptions([]);
            setCoverageLodgeCodes(defaultCoverageLodgeCodes);
            setCoverageLodgeConfigMessage("Using the default lodge/body checklist because the saved configuration could not be loaded.");
        }
    };

    const saveCoverageLodgeConfig = async (selectedCodes = coverageLodgeCodes) => {
        setCoverageLodgeConfigSaving(true);
        setCoverageLodgeConfigMessage("");
        try {
            const res = await fetch("/api/v1/analytics/coverage-lodge-config", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ selectedCodes }),
            });
            if (!res.ok) throw new Error(`Request failed with ${res.status}`);
            const data = await res.json();
            setCoverageLodgeCodes(Array.isArray(data.selectedCodes) ? data.selectedCodes : selectedCodes);
            setCoverageLodgeConfigMessage("Coverage checklist saved.");
        } catch (e) {
            console.error("Coverage lodge config save error:", e);
            setCoverageLodgeConfigMessage("Could not save the coverage checklist.");
        } finally {
            setCoverageLodgeConfigSaving(false);
        }
    };

    useEffect(() => {
        loadArchiveInsights();
        loadExpectedMeetingGaps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, lodgeDocumentTypes, expectedDocumentTypes, expectedEventTypes, coverageLodgeCodes, selectedCoverageLodge]);

    useEffect(() => {
        loadCoverageLodgeConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLodgeSummary = async () => {
        setLodgeSummaryLoading(true);
        setLodgeSummaryError("");

        const qs = new URLSearchParams({ from, to });

        try {
            const res = await fetch(`/api/v1/analytics/lodge-document-summary?${qs.toString()}`);
            if (!res.ok) {
                throw new Error(`Request failed with ${res.status}`);
            }
            const data = await res.json();
            setLodgeSummary(data);
        } catch (e) {
            console.error("Lodge summary load error:", e);
            setLodgeSummary(null);
            setLodgeSummaryError("Lodge summary endpoint is not available yet.");
        } finally {
            setLodgeSummaryLoading(false);
        }
    };

    useEffect(() => {
        if (showLodgeSummary) loadLodgeSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, showLodgeSummary]);

    // ----- Chart data builders -----

    // Daily trend: page views & unique users (SAST labels)
    const dailyLine = useMemo(() => {
        if (!overview?.daily) return null;

        const labels = overview.daily.map(d => toSASTDateLabel(d.date));
        return {
            labels,
            datasets: [
                {
                    type: "line",
                    label: "Page Views",
                    data: overview.daily.map(d => d.page_views),
                    borderWidth: 2,
                    tension: 0.25,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Unique IPs",
                    data: overview.daily.map(d => d.unique_ips),
                    borderWidth: 2,
                    tension: 0.25,
                    yAxisID: "y1",
                }
            ]
        };
    }, [overview]);

    const dailyLineOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" },
            title: { display: true, text: "Daily Page Views & Unique IPs (SAST)" },
        },
        scales: {
            y:  { type: 'linear', position: 'left', beginAtZero: true },
            y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false } }
        }
    };

    // Hour × weekday stacked bars (SAST)
    const hourByDowBar = useMemo(() => {
        if (!overview?.hourByDow) return null;

        const labels = Array.from({length:24}, (_,h) => `${h}:00`);
        const series = Array.from({length:7}, () => Array(24).fill(0));

        overview.hourByDow.forEach(({ hour, dow, count }) => {
            if (hour >=0 && hour <=23 && dow >=0 && dow <=6) {
                series[dow][hour] = count;
            }
        });

        const datasets = series.map((arr, i) => ({
            label: weekday[i],
            data: arr,
            stack: 'dow',
        }));

        return { labels, datasets };
    }, [overview]);

    const hourByDowOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" },
            title: { display: true, text: "Activity by Hour × Weekday (SAST)" },
        },
        scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } }
    };

    // Top lists (from overview)
    const topPaths = overview?.topPaths || [];
    const topTypes = overview?.topTypes || [];
    const topCountries = overview?.topCountries || [];
    const kpis = overview?.kpis || {
        total_events: 0, total_sessions: 0, total_users: 0, total_unique_ips: 0, total_page_views: 0
    };

    // Recent users bar (horizontal)
    const recentUsersBar = useMemo(() => {
        if (!extra?.recentUsers?.length) return null;
        const labels = extra.recentUsers.map(u => {
            const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
            return name || `User #${u.user_id}`;
        });
        const data = extra.recentUsers.map(u => u.events);
        return {
            labels,
            datasets: [{ label: "Events (period)", data }],
        };
    }, [extra]);

    const recentUsersBarOpts = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: "Top Recent Users (by Events)" },
        },
        scales: { x: { beginAtZero: true } },
    };

    // Devices doughnut
    const devicesDoughnut = useMemo(() => {
        if (!extra?.devices?.length) return null;
        return {
            labels: extra.devices.map(d => d.device),
            datasets: [{ data: extra.devices.map(d => d.count) }],
        };
    }, [extra]);

    const researchLocations = useMemo(() => {
        return (researchContext?.locations || []).map((row) => {
            const country = String(row.country || "").trim().toUpperCase();
            const point = locationPoint(row, country);
            const label = [row.city, row.region, countryName(country)].filter((part) => part && part !== "Unknown").join(", ") || countryName(country);
            return {
                ...row,
                label,
                point,
                sessions: Number(row.sessions || 0),
                visitors: Number(row.visitors || row.sessions || 0),
                interactions: Number(row.interactions || 0),
            };
        });
    }, [researchContext]);

    const mappedResearchLocations = researchLocations.filter((row) => row.point && (row.visitors > 0 || row.sessions > 0));
    const unknownResearchLocationSessions = researchLocations
        .filter((row) => !row.point)
        .reduce((sum, row) => sum + Number(row.sessions || 0), 0);
    const researchDeviceRows = useMemo(() => (
        (researchContext?.devices || [])
            .map((row) => ({
                device: row.device || "Unknown",
                sessions: Number(row.sessions || 0),
                visitors: Number(row.visitors || row.sessions || 0),
                interactions: Number(row.interactions || 0),
            }))
            .filter((row) => row.visitors > 0 || row.sessions > 0 || row.interactions > 0)
    ), [researchContext]);

    const researchDevicesDoughnut = useMemo(() => {
        if (!researchDeviceRows.length) return null;
        return {
            labels: researchDeviceRows.map((row) => row.device),
            datasets: [{
                data: researchDeviceRows.map((row) => row.visitors || row.sessions || row.interactions),
                backgroundColor: ["#1f3b57", "#2f6c45", "#9a7a2f", "#7c4d9e", "#64748b"],
                borderColor: "#ffffff",
                borderWidth: 2,
            }],
        };
    }, [researchDeviceRows]);

    // NEW: Downloads by User (horizontal bar)
    const dlByUserBar = useMemo(() => {
        if (!dlByUser?.length) return null;
        const labels = dlByUser.map(u => {
            const name =
                [u.first_name, u.last_name].filter(Boolean).join(" ").trim()
                || u.display
                || (u.user_id ? `User #${u.user_id}` : "—");
            return name;
        });
        const data = dlByUser.map(u => u.downloads);
        return { labels, datasets: [{ label: "Downloads", data }] };
    }, [dlByUser]);

    const dlByUserBarOpts = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: "Top Users by Downloads" },
        },
        scales: { x: { beginAtZero: true } },
    };

    // Downloads by Document (vertical bar)
    const dlByDocBar = useMemo(() => {
        if (!dlByDoc?.length) return null;
        const labels = dlByDoc.map(d => d.lodge || "Unknown");
        const data = dlByDoc.map(d => d.downloads);
        return { labels, datasets: [{ label: "Downloads", data }] };
    }, [dlByDoc]);

    const dlByDocBarOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: "Top Entities by Downloads" },
            tooltip: {
                callbacks: {
                    title(items) { return (items?.[0]?.label || "").trim(); }
                }
            }
        },
        scales: { y: { beginAtZero: true } },
    };

    const archiveTrendLine = useMemo(() => {
        if (!archiveInsights?.documentsByMonth?.length) return null;
        return {
            labels: archiveInsights.documentsByMonth.map(item => monthKeyToLabel(item.month)),
            datasets: [
                {
                    type: "line",
                    label: "Documents",
                    data: archiveInsights.documentsByMonth.map(item => item.count),
                    borderWidth: 2,
                    tension: 0.25,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Cumulative",
                    data: archiveInsights.documentsByMonth.map(item => item.cumulative_count || item.cumulative || 0),
                    borderWidth: 2,
                    tension: 0.2,
                    yAxisID: "y1",
                },
            ],
        };
    }, [archiveInsights]);

    const archiveTrendOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" },
            title: { display: true, text: "Archive Growth" },
        },
        scales: {
            y: { type: "linear", position: "left", beginAtZero: true },
            y1: { type: "linear", position: "right", beginAtZero: true, grid: { drawOnChartArea: false } },
        },
    };

    const documentTypeBar = useMemo(() => {
        if (!archiveInsights?.documentsByType?.length) return null;
        return {
            labels: archiveInsights.documentsByType.map(item => item.document_type || item.description || item.code),
            datasets: [{ label: "Documents", data: archiveInsights.documentsByType.map(item => item.count) }],
        };
    }, [archiveInsights]);

    const documentTypeBarOpts = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: "Document Type Mix" },
        },
        scales: { x: { beginAtZero: true } },
    };

    const lodgeCoverageBar = useMemo(() => {
        if (!archiveInsights?.lodgeCoverage?.length) return null;
        const topCoverage = archiveInsights.lodgeCoverage.slice(0, 12);
        return {
            labels: topCoverage.map(item => item.lodge),
            datasets: [{ label: "Coverage %", data: topCoverage.map(item => item.coverage_percent || 0) }],
        };
    }, [archiveInsights]);

    const lodgeCoverageBarOpts = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: true, text: "Best Lodge Coverage" },
        },
        scales: { x: { beginAtZero: true, max: 100 } },
    };

    const lodgeHeatmap = useMemo(() => {
        const rawItems = Array.isArray(lodgeSummary?.items)
            ? lodgeSummary.items
            : Array.isArray(lodgeSummary)
                ? lodgeSummary
                : [];

        if (!rawItems.length) {
            return { columns: [], rows: [], max: 0 };
        }

        const looksPivoted = rawItems.some(item =>
            Object.prototype.hasOwnProperty.call(item, "agendas")
            || Object.prototype.hasOwnProperty.call(item, "minutes")
            || Object.prototype.hasOwnProperty.call(item, "total_agendas_and_minutes")
        );

        if (looksPivoted) {
            const columns = [
                { key: "agendas", label: "Agendas" },
                { key: "minutes", label: "Minutes" },
                { key: "total_agendas_and_minutes", label: "Total" },
            ];
            const rows = rawItems.map(item => ({
                lodge: item.lodge || "Unknown",
                values: columns.reduce((acc, column) => {
                    acc[column.key] = Number(item[column.key] || 0);
                    return acc;
                }, {}),
            }));
            const max = Math.max(0, ...rows.flatMap(row => columns.map(column => row.values[column.key])));
            return { columns, rows, max };
        }

        const columnLabels = lodgeSummary?.documentTypes?.length
            ? lodgeSummary.documentTypes
            : [...new Set(rawItems.map(item => item.document_type || item.documentType || item.type).filter(Boolean))];
        const columns = columnLabels.map(label => ({
            key: String(label).toLowerCase(),
            label: normalizeDocTypeLabel(label),
        }));
        const rowsByLodge = new Map();

        rawItems.forEach(item => {
            const lodge = item.lodge || "Unknown";
            const docType = String(item.document_type || item.documentType || item.type || "unknown").toLowerCase();
            const count = Number(item.count || item.total || item.documents || 0);
            if (!rowsByLodge.has(lodge)) rowsByLodge.set(lodge, {});
            rowsByLodge.get(lodge)[docType] = (rowsByLodge.get(lodge)[docType] || 0) + count;
        });

        const rows = [...rowsByLodge.entries()]
            .map(([lodge, values]) => ({ lodge, values }))
            .sort((a, b) => {
                const aTotal = Object.values(a.values).reduce((sum, value) => sum + value, 0);
                const bTotal = Object.values(b.values).reduce((sum, value) => sum + value, 0);
                return bTotal - aTotal || a.lodge.localeCompare(b.lodge);
            });
        const max = Math.max(0, ...rows.flatMap(row => columns.map(column => row.values[column.key] || 0)));

        return { columns, rows, max };
    }, [lodgeSummary]);

    const monthlyCoverageHeatmap = useMemo(() => {
        const rawItems = Array.isArray(lodgeSummary?.monthlyCoverage)
            ? lodgeSummary.monthlyCoverage
            : Array.isArray(lodgeSummary?.coverage)
                ? lodgeSummary.coverage
                : [];
        const monthKeys = lodgeSummary?.months?.length
            ? lodgeSummary.months
            : getMonthKeysBetween(from, to);

        const resolvedGranularity = resolveCoverageGranularity(monthKeys.length, coverageGranularity);
        const periods = [...new Set(monthKeys.map(month => getPeriodKey(month, resolvedGranularity)))];

        if (!rawItems.length || !monthKeys.length) {
            return { periods, rows: [], resolvedGranularity };
        }

        const expectedPerMonth = Math.max(1, lodgeDocumentTypes.length);
        const rowsByLodge = new Map();

        rawItems.forEach(item => {
            const lodge = item.lodge || "Unknown";
            const month = item.month || item.month_key || item.monthKey;
            if (!month) return;

            const agendaCount = Number(item.agendas || item.agenda_count || 0);
            const minutesCount = Number(item.minutes || item.minute_count || item.minutes_count || 0);
            const completed = item.completed_document_types != null
                ? Number(item.completed_document_types)
                : Number(item.completed || 0)
                    || Number(agendaCount > 0)
                    + Number(minutesCount > 0);
            const expected = Number(item.expected_document_types || item.expected || expectedPerMonth);

            if (!rowsByLodge.has(lodge)) rowsByLodge.set(lodge, {});
            const period = getPeriodKey(month, resolvedGranularity);
            const existing = rowsByLodge.get(lodge)[period] || {
                agendas: 0,
                minutes: 0,
                completed: 0,
                expected: 0,
                totalDocuments: 0,
                monthCount: 0,
                percent: 0,
                details: [],
            };
            rowsByLodge.get(lodge)[period] = {
                monthCount: existing.monthCount + 1,
                agendas: existing.agendas + agendaCount,
                minutes: existing.minutes + minutesCount,
                completed: existing.completed + completed,
                expected: existing.expected + expected,
                totalDocuments: existing.totalDocuments + Number(item.total_documents || item.documents || agendaCount + minutesCount || 0),
                percent: 0,
                details: [
                    ...existing.details,
                    {
                        month,
                        expectedEventTypes: item.expected_event_types || item.expectedEventTypes || [],
                        scheduleNote: item.schedule_note || item.scheduleNote || "",
                        documentTypes: item.document_types || item.documentTypes || [],
                        agendas: agendaCount,
                        minutes: minutesCount,
                    }
                ],
            };
        });

        rowsByLodge.forEach(periodValues => {
            Object.keys(periodValues).forEach(period => {
                const cell = periodValues[period];
                cell.percent = cell.expected > 0 ? Math.min(100, (cell.completed / cell.expected) * 100) : 0;
            });
        });

        const rows = [...rowsByLodge.entries()]
            .map(([lodge, values]) => {
                const totalPercent = periods.reduce((sum, period) => sum + (values[period]?.percent || 0), 0);
                return {
                    lodge,
                    values,
                    averagePercent: periods.length ? totalPercent / periods.length : 0,
                };
            })
            .sort((a, b) => b.averagePercent - a.averagePercent || a.lodge.localeCompare(b.lodge));

        return { periods, rows, resolvedGranularity };
    }, [coverageGranularity, from, lodgeDocumentTypes, lodgeSummary, to]);

    const expectedMeetingHeatmap = useMemo(() => {
        const rawRows = Array.isArray(expectedMeetingGaps?.rows) ? expectedMeetingGaps.rows : [];
        const selectedCodes = new Set(coverageLodgeCodes);
        const filteredRows = selectedCodes.size
            ? rawRows.filter(item => selectedCodes.has(item.lodge_code))
            : [];
        const monthKeys = expectedMeetingGaps?.months?.length
            ? expectedMeetingGaps.months
            : getMonthKeysBetween(from, to);
        const rowsByLodge = new Map();

        filteredRows.forEach(item => {
            const lodge = item.lodge || item.schedule_label || "Unknown";
            const month = item.month;
            if (!month) return;
            if (!rowsByLodge.has(lodge)) {
                rowsByLodge.set(lodge, {
                    lodge,
                    stream: item.stream || "",
                    values: {},
                    expectedCount: 0,
                    percentTotal: 0,
                });
            }
            const row = rowsByLodge.get(lodge);
            const percent = Number(item.coverage_percent || 0);
            row.values[month] = {
                ...item,
                percent,
                agendas: Number(item.agendas || 0),
                minutes: Number(item.minutes || 0),
                totalDocuments: Number(item.total_documents || 0),
            };
            row.expectedCount += 1;
            row.percentTotal += percent;
        });

        const rows = [...rowsByLodge.values()]
            .map(row => ({
                ...row,
                averagePercent: row.expectedCount ? row.percentTotal / row.expectedCount : 0,
            }))
            .sort((a, b) => b.averagePercent - a.averagePercent || a.lodge.localeCompare(b.lodge));

        const summary = {
            expected_slots: filteredRows.length,
            complete_slots: filteredRows.filter(row => row.status === "complete").length,
            partial_slots: filteredRows.filter(row => row.status === "partial").length,
            missing_slots: filteredRows.filter(row => row.status === "missing").length,
            open_gaps: filteredRows.filter(row => row.missing_document_types?.length > 0).length,
        };

        return { months: monthKeys, rows, summary };
    }, [coverageLodgeCodes, expectedMeetingGaps, from, to]);

    const visibleCoverageLodgeOptions = useMemo(() => {
        const needle = coverageLodgeSearch.trim().toLowerCase();
        if (!needle) return coverageLodgeOptions;
        return coverageLodgeOptions.filter(option =>
            option.lodge.toLowerCase().includes(needle)
            || String(option.lodge_code || "").toLowerCase().includes(needle)
            || String(option.stream || "").toLowerCase().includes(needle)
        );
    }, [coverageLodgeOptions, coverageLodgeSearch]);

    const quickCoverageLodgeOptions = useMemo(() => {
        const selectedCodes = new Set(coverageLodgeCodes);
        return coverageLodgeOptions
            .filter(option => selectedCodes.has(option.lodge_code))
            .map(option => ({
                value: option.lodge_code,
                label: option.lodge,
                stream: option.stream,
            }));
    }, [coverageLodgeCodes, coverageLodgeOptions]);

    useEffect(() => {
        if (selectedCoverageLodge && !coverageLodgeCodes.includes(selectedCoverageLodge.value)) {
            setSelectedCoverageLodge(null);
        }
    }, [coverageLodgeCodes, selectedCoverageLodge]);

    const getHeatmapCellStyle = (value, max) => {
        if (!max || !value) return {};
        const intensity = Math.max(0.12, value / max);
        return {
            backgroundColor: `rgba(20, 120, 120, ${0.16 + intensity * 0.62})`,
            color: intensity > 0.56 ? "#ffffff" : "#0f172a",
        };
    };

    const getCoverageCellClassName = (cell) => {
        if (!cell || cell.percent <= 0) return "coverage-missing";
        if (cell.percent >= 100) return "coverage-complete";
        return "coverage-partial";
    };

    const getExpectedGapCellClassName = (cell) => {
        if (!cell) return "expected-gap-none";
        if (cell.status === "complete" || cell.percent >= 100) return "coverage-complete";
        if (cell.status === "partial" || cell.percent > 0) return "coverage-partial";
        return "coverage-missing";
    };

    const setQuickRange = (days) => {
        const end = new Date();
        const start = new Date(Date.now() - (days - 1) * 86400000);
        setFrom(start.toISOString().slice(0, 10));
        setTo(end.toISOString().slice(0, 10));
    };

    const exportCsv = (filename, rows) => {
        if (!rows?.length) return;
        const headers = Object.keys(rows[0]);
        const escapeCell = (value) => {
            const text = value == null ? "" : String(value);
            return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        };
        const csv = [
            headers.join(","),
            ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(",")),
        ].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const rangeLabel = `${from} to ${to}`;
    const topPath = topPaths[0];
    const topType = topTypes[0];
    const topUser = extra?.recentUsers?.[0];
    const topDownloader = dlByUser?.[0];
    const topDownloadDoc = dlByDoc?.[0];
    const avgCoverage = Number(archiveInsights?.kpis?.avg_coverage_percent || 0);
    const openGaps = Number(archiveInsights?.kpis?.open_gaps || 0);
    const activeInsightCards = [
        {
            label: "Most used area",
            value: topPath?.path || "No path data",
            meta: topPath ? `${formatCount(topPath.count)} events` : "Awaiting usage",
        },
        {
            label: "Most common action",
            value: topType?.type || "No actions yet",
            meta: topType ? `${formatCount(topType.count)} logged` : "No interaction data",
        },
        {
            label: "Most active user",
            value: topUser
                ? [topUser.first_name, topUser.last_name].filter(Boolean).join(" ").trim() || `User #${topUser.user_id}`
                : "No user activity",
            meta: topUser ? `${formatCount(topUser.events)} events` : "No matching users",
        },
        {
            label: "Top download demand",
            value: topDownloadDoc?.lodge || "No downloads",
            meta: topDownloadDoc ? `${formatCount(topDownloadDoc.downloads)} downloads` : "No download activity",
        },
    ];

    const actionCards = [
        {
            title: "Archive coverage",
            value: `${formatPercent(avgCoverage)} complete`,
            body: openGaps > 0
                ? `${formatCount(openGaps)} selected lodge/month gaps need attention.`
                : "No selected coverage gaps in this period.",
            tone: openGaps > 0 ? "warn" : "good",
            action: "Show Lodge Summary",
            onClick: () => {
                setShowLodgeSummary(true);
                setActiveLens("archive");
            },
        },
        {
            title: "Reader demand",
            value: topDownloader
                ? `${formatCount(topDownloader.downloads)} downloads`
                : "No downloads",
            body: topDownloader
                ? `${topDownloader.display || [topDownloader.first_name, topDownloader.last_name].filter(Boolean).join(" ").trim() || "A user"} is the leading downloader.`
                : "Downloads will appear here once documents are opened from the archive.",
            tone: topDownloader ? "info" : "quiet",
            action: "Export Downloads",
            onClick: () => exportCsv(
                `download-users-${from}-${to}.csv`,
                dlByUser.map((row) => ({
                    user: row.display || [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || row.user_id || "",
                    downloads: row.downloads,
                }))
            ),
        },
        {
            title: "System use",
            value: `${formatCount(kpis.total_sessions)} sessions`,
            body: `${formatCount(kpis.total_page_views)} page views from ${formatCount(kpis.total_unique_ips)} unique IPs and ${formatCount(kpis.total_users)} logged-in users.`,
            tone: "info",
            action: "Export Activity",
            onClick: () => exportCsv(
                `recent-activity-${from}-${to}.csv`,
                recent.map((row) => ({
                    time_sast: toSASTDateTime(row.timestamp),
                    user: (row.first_name || row.last_name) ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : row.user_id || "",
                    type: row.interaction_type,
                    path: row.request_path,
                    session: row.session_id,
                    ip: row.ip,
                }))
            ),
        },
    ];

    const showOverview = activeLens === "overview";
    const showArchive = showOverview || activeLens === "archive";
    const showUsage = showOverview || activeLens === "usage";
    const showDownloads = showOverview || activeLens === "downloads";
    const showActivity = showOverview || activeLens === "activity";

    return (
        <div className="analytics-page">
            <section className="analytics-hero">
                <div>
                    <p className="analytics-kicker">Archive intelligence</p>
                    <h1>Analytics</h1>
                    <p>
                        Understand how the archive is being used, which records are in demand, and where the collection still needs attention.
                    </p>
                </div>
                <div className="analytics-range-card">
                    <span>Reporting range</span>
                    <strong>{rangeLabel}</strong>
                    <small>{path || userId ? "Filtered view" : "All tracked activity"}</small>
                </div>
            </section>

            <section className="analytics-control-panel">
                <div className="quick-range-row">
                    {[7, 30, 90, 365].map((days) => (
                        <button key={days} className="btn" type="button" onClick={() => setQuickRange(days)}>
                            {days === 365 ? "1 year" : `${days} days`}
                        </button>
                    ))}
                    <button
                        className="btn"
                        type="button"
                        onClick={() => {
                            setPath("");
                            setUserId("");
                            setQuickRange(30);
                        }}
                    >
                        Reset
                    </button>
                </div>

                <div className="filters analytics-filters">
                    <div>
                        <label>From</label>
                        <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
                    </div>
                    <div>
                        <label>To</label>
                        <input type="date" value={to} onChange={e => setTo(e.target.value)} />
                    </div>
                    <div className="w240">
                        <label>Path</label>
                        <Select
                            options={pathsDropdown}
                            value={pathsDropdown.find(p => p.value === path) || null}
                            onChange={opt => setPath(opt?.value || "")}
                            isClearable
                            placeholder="All paths"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className="w240">
                        <label>Uploader / User</label>
                        <Select
                            options={usersDropdown}
                            value={usersDropdown.find(u => u.value === userId) || null}
                            onChange={opt => setUserId(opt?.value || "")}
                            isClearable
                            placeholder="All users"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <button className="btn btn-primary-analytics" onClick={load} disabled={loading}>Refresh</button>
                    <button
                        className={`btn ${showLodgeSummary ? "btn-active" : ""}`}
                        onClick={() => setShowLodgeSummary(value => !value)}
                        disabled={lodgeSummaryLoading}
                    >
                        Lodge Summary
                    </button>
                </div>
            </section>

            {/* KPI cards */}
            <div className="kpi-row">
                <div className="kpi">
                    <div className="kpi-label">Events</div>
                    <div className="kpi-value">{kpis.total_events?.toLocaleString?.() ?? kpis.total_events}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Sessions</div>
                    <div className="kpi-value">{kpis.total_sessions?.toLocaleString?.() ?? kpis.total_sessions}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Unique IPs</div>
                    <div className="kpi-value">{kpis.total_unique_ips?.toLocaleString?.() ?? kpis.total_unique_ips}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Logged-in Users</div>
                    <div className="kpi-value">{kpis.total_users?.toLocaleString?.() ?? kpis.total_users}</div>
                </div>
                <div className="kpi">
                    <div className="kpi-label">Page Views</div>
                    <div className="kpi-value">{kpis.total_page_views?.toLocaleString?.() ?? kpis.total_page_views}</div>
                </div>
            </div>

            <section className="analytics-insight-strip">
                {activeInsightCards.map((card) => (
                    <article className="analytics-insight-card" key={card.label}>
                        <span>{card.label}</span>
                        <strong title={card.value}>{card.value}</strong>
                        <small>{card.meta}</small>
                    </article>
                ))}
            </section>

            <section className="analytics-action-grid">
                {actionCards.map((card) => (
                    <article className={`analytics-action-card ${card.tone}`} key={card.title}>
                        <span>{card.title}</span>
                        <strong>{card.value}</strong>
                        <p>{card.body}</p>
                        <button className="btn" type="button" onClick={card.onClick}>{card.action}</button>
                    </article>
                ))}
            </section>

            <nav className="analytics-lens-tabs" aria-label="Analytics sections">
                {[
                    ["overview", "Overview"],
                    ["archive", "Archive Health"],
                    ["usage", "Usage"],
                    ["downloads", "Downloads"],
                    ["activity", "Activity Log"],
                ].map(([value, label]) => (
                    <button
                        key={value}
                        className={activeLens === value ? "active" : ""}
                        type="button"
                        onClick={() => setActiveLens(value)}
                    >
                        {label}
                    </button>
                ))}
            </nav>

            {showArchive && <section className="archive-analysis-panel analytics-section-panel">
                <div className="archive-analysis-header">
                    <div>
                        <h2>Archive Analysis</h2>
                        <p>{from} to {to}</p>
                    </div>
                    <button className="btn" onClick={loadArchiveInsights} disabled={archiveInsightsLoading}>
                        Refresh Analysis
                    </button>
                </div>

                {archiveInsightsLoading && <p>Loading archive analysis…</p>}
                {archiveInsightsError && <div className="analytics-alert">{archiveInsightsError}</div>}

                {!archiveInsightsLoading && !archiveInsightsError && archiveInsights && (
                    <>
                        <div className="kpi-row archive-kpi-row">
                            <div className="kpi">
                                <div className="kpi-label">Documents</div>
                                <div className="kpi-value">{formatCount(archiveInsights.kpis?.total_documents)}</div>
                            </div>
                            <div className="kpi">
                                <div className="kpi-label">Lodges</div>
                                <div className="kpi-value">{formatCount(archiveInsights.kpis?.total_lodges)}</div>
                            </div>
                            <div className="kpi">
                                <div className="kpi-label">Selected Type Coverage</div>
                                <div className="kpi-value">{formatPercent(archiveInsights.kpis?.avg_coverage_percent)}</div>
                            </div>
                            <div className="kpi">
                                <div className="kpi-label">Open Gaps</div>
                                <div className="kpi-value">{formatCount(archiveInsights.kpis?.open_gaps)}</div>
                            </div>
                        </div>

                        <div className="grid-2">
                            <div className="chart-container">
                                <div className="chart-wrapper">
                                    {archiveTrendLine ? (
                                        <Line data={archiveTrendLine} options={archiveTrendOpts} />
                                    ) : (
                                        <div className="analytics-empty">No archive growth data for this range.</div>
                                    )}
                                </div>
                            </div>
                            <div className="chart-container">
                                <div className="chart-wrapper">
                                    {documentTypeBar ? (
                                        <Bar data={documentTypeBar} options={documentTypeBarOpts} />
                                    ) : (
                                        <div className="analytics-empty">No document type data for this range.</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid-2">
                            <div className="chart-container">
                                <div className="chart-wrapper">
                                    {lodgeCoverageBar ? (
                                        <Bar data={lodgeCoverageBar} options={lodgeCoverageBarOpts} />
                                    ) : (
                                        <div className="analytics-empty">No lodge coverage data for this range.</div>
                                    )}
                                </div>
                            </div>
                            <div className="archive-gap-table">
                                <h3>Priority Gaps</h3>
                                {archiveInsights.gaps?.length ? (
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Lodge</th>
                                            <th>Period</th>
                                            <th>Missing</th>
                                            <th>Coverage</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {archiveInsights.gaps.slice(0, 12).map((gap, index) => (
                                            <tr key={`${gap.lodge}-${gap.month || gap.period}-${index}`}>
                                                <td title={gap.lodge}>{gap.lodge}</td>
                                                <td>{gap.month || gap.period}</td>
                                                <td>{Array.isArray(gap.missing_document_types) ? gap.missing_document_types.join(", ") : gap.missing_document_types}</td>
                                                <td>{formatPercent(gap.coverage_percent)}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="analytics-empty">No priority gaps for the selected range and document types.</div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                <div className="expected-meeting-panel">
                    <div className="analytics-section-heading">
                        <div>
                            <h3>Monthly Lodge/Body Document Coverage</h3>
                            <p>Every lodge/body is listed monthly. Use document and event type filters to decide what counts as covered.</p>
                        </div>
                        <div className="expected-gap-controls">
                            <div className="w240">
                                <label>Document type</label>
                                <Select
                                    options={expectedDocumentTypeOptions}
                                    value={expectedDocumentTypes}
                                    onChange={opts => setExpectedDocumentTypes(opts?.length ? opts : defaultLodgeDocumentTypeOptions)}
                                    isMulti
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="w240">
                                <label>Event type</label>
                                <Select
                                    options={expectedEventTypeOptions}
                                    value={expectedEventTypes}
                                    onChange={opts => setExpectedEventTypes(opts || [])}
                                    isMulti
                                    isClearable
                                    placeholder="All event types"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div className="w240">
                                <label>Quick lodge/body</label>
                                <Select
                                    options={quickCoverageLodgeOptions}
                                    value={selectedCoverageLodge}
                                    onChange={opt => setSelectedCoverageLodge(opt || null)}
                                    isClearable
                                    placeholder="All selected bodies"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <button className="btn" onClick={loadExpectedMeetingGaps} disabled={expectedMeetingGapsLoading}>
                                Refresh
                            </button>
                        </div>
                    </div>

                    {expectedMeetingGapsLoading && <p>Loading expected agenda and minutes gaps…</p>}
                    {expectedMeetingGapsError && <div className="analytics-alert">{expectedMeetingGapsError}</div>}
                    {!expectedMeetingGapsLoading && !expectedMeetingGapsError && expectedMeetingGaps && (
                        <>
                            <div className="coverage-lodge-config">
                                <div className="coverage-lodge-config-header">
                                    <div>
                                        <h4>Lodge/body checklist</h4>
                                        <p>{coverageLodgeCodes.length} selected from {coverageLodgeOptions.length || "the"} lodge/body table.</p>
                                    </div>
                                    <div className="coverage-lodge-config-actions">
                                        <button type="button" className="btn secondary" onClick={() => setShowCoverageLodgePicker(value => !value)}>
                                            {showCoverageLodgePicker ? "Hide Checklist" : "Configure Checklist"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn"
                                            disabled={coverageLodgeConfigSaving}
                                            onClick={() => saveCoverageLodgeConfig()}
                                        >
                                            {coverageLodgeConfigSaving ? "Saving..." : "Save Checklist"}
                                        </button>
                                    </div>
                                </div>
                                {coverageLodgeConfigMessage && <div className="coverage-lodge-config-message">{coverageLodgeConfigMessage}</div>}
                                {showCoverageLodgePicker && (
                                    <div className="coverage-lodge-picker">
                                        <div className="coverage-lodge-picker-toolbar">
                                            <input
                                                type="search"
                                                value={coverageLodgeSearch}
                                                onChange={event => setCoverageLodgeSearch(event.target.value)}
                                                placeholder="Search lodge, chapter, council, preceptory..."
                                            />
                                            <button type="button" className="btn secondary" onClick={() => setCoverageLodgeCodes(defaultCoverageLodgeCodes)}>
                                                SANIC Set
                                            </button>
                                            <button type="button" className="btn secondary" onClick={() => setCoverageLodgeCodes(coverageLodgeOptions.map(option => option.lodge_code))}>
                                                All
                                            </button>
                                            <button type="button" className="btn secondary" onClick={() => setCoverageLodgeCodes([])}>
                                                Clear
                                            </button>
                                        </div>
                                        <div className="coverage-lodge-checklist">
                                            {visibleCoverageLodgeOptions.map(option => {
                                                const checked = coverageLodgeCodes.includes(option.lodge_code);
                                                return (
                                                    <label className={`coverage-lodge-check stream-${option.stream || "other"}`} key={option.lodge_code}>
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={event => {
                                                                if (event.target.checked) {
                                                                    setCoverageLodgeCodes(prev => [...new Set([...prev, option.lodge_code])]);
                                                                } else {
                                                                    setCoverageLodgeCodes(prev => prev.filter(code => code !== option.lodge_code));
                                                                }
                                                            }}
                                                        />
                                                        <span>
                                                            <strong>{option.lodge}</strong>
                                                            <em>{option.stream || "other"}</em>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="expected-gap-summary">
                                <div><span>Monthly cells</span><strong>{formatCount(expectedMeetingHeatmap.summary?.expected_slots)}</strong></div>
                                <div><span>Complete</span><strong>{formatCount(expectedMeetingHeatmap.summary?.complete_slots)}</strong></div>
                                <div><span>Partial</span><strong>{formatCount(expectedMeetingHeatmap.summary?.partial_slots)}</strong></div>
                                <div><span>Missing selected</span><strong>{formatCount(expectedMeetingHeatmap.summary?.missing_slots)}</strong></div>
                            </div>

                            {expectedMeetingHeatmap.rows?.length ? (
                                <div className="expected-gap-heatmap-scroll">
                                    <div
                                        className="expected-gap-heatmap"
                                        style={{ gridTemplateColumns: `minmax(220px, 1.5fr) repeat(${expectedMeetingHeatmap.months.length}, minmax(62px, 1fr)) minmax(78px, .7fr)` }}
                                    >
                                        <div className="lodge-heatmap-heading">Lodge/body</div>
                                        {expectedMeetingHeatmap.months.map(month => (
                                            <div className="lodge-heatmap-heading coverage-month-heading" key={month}>
                                                {periodKeyToLabel(month, "month")}
                                            </div>
                                        ))}
                                        <div className="lodge-heatmap-heading coverage-month-heading">Avg</div>
                                        {expectedMeetingHeatmap.rows.map(row => (
                                            <React.Fragment key={row.lodge}>
                                                <div className="lodge-heatmap-lodge" title={row.lodge}>
                                                    <span className={`expected-gap-lodge-name stream-${row.stream || "other"}`}>{row.lodge}</span>
                                                    {row.stream && <span className="expected-gap-stream">{row.stream}</span>}
                                                </div>
                                                {expectedMeetingHeatmap.months.map(month => {
                                                    const cell = row.values[month];
                                                    return (
                                                        <div
                                                            className={`coverage-cell ${getExpectedGapCellClassName(cell)}`}
                                                            key={`${row.lodge}-${month}`}
                                                            role={cell ? "button" : undefined}
                                                            tabIndex={cell ? 0 : undefined}
                                                            title={cell
                                                                ? `${row.lodge} ${month}: ${formatPercent(cell.percent)} complete (${cell.agendas || 0} agenda, ${cell.minutes || 0} minutes)`
                                                                : `${row.lodge} ${month}: no expected meeting in schedule`}
                                                            onClick={() => cell && setSelectedExpectedGapCell({ lodge: row.lodge, month, cell })}
                                                            onKeyDown={event => {
                                                                if (cell && (event.key === "Enter" || event.key === " ")) {
                                                                    event.preventDefault();
                                                                    setSelectedExpectedGapCell({ lodge: row.lodge, month, cell });
                                                                }
                                                            }}
                                                        >
                                                            <span>{cell ? formatPercent(cell.percent) : ""}</span>
                                                        </div>
                                                    );
                                                })}
                                                <div className="coverage-cell coverage-average">
                                                    <span>{formatPercent(row.averagePercent)}</span>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <div className="coverage-legend">
                                        <span><i className="coverage-missing" /> Missing selected</span>
                                        <span><i className="coverage-partial" /> Partial</span>
                                        <span><i className="coverage-complete" /> Complete</span>
                                    </div>
                                    {selectedExpectedGapCell && (
                                        <div className="coverage-detail-panel">
                                            <div>
                                                <h3>{selectedExpectedGapCell.lodge}</h3>
                                                <p>{selectedExpectedGapCell.month} · {formatPercent(selectedExpectedGapCell.cell?.percent)} complete</p>
                                            </div>
                                            <div className="coverage-detail-grid">
                                                <div className="coverage-detail-item">
                                                    <strong>Expected meeting type</strong>
                                                    <span>{expectedEventTypes.length
                                                        ? expectedEventTypes.map(option => option.label).join(", ")
                                                        : selectedExpectedGapCell.cell?.expected_event_types?.length
                                                        ? selectedExpectedGapCell.cell.expected_event_types.join(", ")
                                                        : selectedExpectedGapCell.cell?.expected_type || "Meeting"}</span>
                                                    <span>{selectedExpectedGapCell.cell?.schedule_note || "Schedule note not recorded"}</span>
                                                </div>
                                                <div className="coverage-detail-item">
                                                    <strong>Agenda/minutes found</strong>
                                                    <span>{selectedExpectedGapCell.cell?.document_event_types?.length
                                                        ? selectedExpectedGapCell.cell.document_event_types.join(", ")
                                                        : "No agenda/minutes documents found"}</span>
                                                    <span>{formatCount(selectedExpectedGapCell.cell?.agendas)} agenda · {formatCount(selectedExpectedGapCell.cell?.minutes)} minutes</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="analytics-empty">No expected schedule rows for this range.</div>
                            )}
                        </>
                    )}
                </div>
            </section>}

            {loading && <p>Loading…</p>}

            {!loading && overview && (
                <>
                    {showArchive && showLodgeSummary && (
                        <section className="lodge-summary-panel">
                            <div className="lodge-summary-header">
                                <div>
                                    <h2>Lodge Document Summary</h2>
                                    <p>{from} to {to}</p>
                                </div>
                                <div className="lodge-summary-controls">
                                    <div className="lodge-view-toggle" aria-label="Lodge summary view">
                                        <button
                                            className={lodgeSummaryView === "coverage" ? "active" : ""}
                                            type="button"
                                            onClick={() => setLodgeSummaryView("coverage")}
                                        >
                                            Monthly Coverage
                                        </button>
                                        <button
                                            className={lodgeSummaryView === "counts" ? "active" : ""}
                                            type="button"
                                            onClick={() => setLodgeSummaryView("counts")}
                                        >
                                            Counts
                                        </button>
                                    </div>
                                    <div className="w160">
                                        <label>Coverage Scale</label>
                                        <Select
                                            options={coverageGranularityOptions}
                                            value={coverageGranularityOptions.find(option => option.value === coverageGranularity)}
                                            onChange={opt => setCoverageGranularity(opt?.value || "auto")}
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                </div>
                            </div>

                            {lodgeSummaryLoading && <p>Loading lodge summary…</p>}
                            {lodgeSummaryError && <div className="analytics-alert">{lodgeSummaryError}</div>}
                            {!lodgeSummaryLoading && !lodgeSummaryError && lodgeSummaryView === "coverage" && monthlyCoverageHeatmap.rows.length === 0 && (
                                <div className="analytics-empty">
                                    No monthly coverage data for this range. The endpoint needs to return <code>monthlyCoverage</code>.
                                </div>
                            )}
                            {!lodgeSummaryLoading && !lodgeSummaryError && lodgeSummaryView === "coverage" && monthlyCoverageHeatmap.rows.length > 0 && (
                                <div className="lodge-coverage-scroll">
                                    <div
                                        className="lodge-coverage-grid"
                                        style={{ gridTemplateColumns: `minmax(180px, 1.3fr) repeat(${monthlyCoverageHeatmap.periods.length}, minmax(58px, 1fr)) minmax(78px, .7fr)` }}
                                    >
                                        <div className="lodge-heatmap-heading">Lodge</div>
                                        {monthlyCoverageHeatmap.periods.map(period => (
                                            <div className="lodge-heatmap-heading coverage-month-heading" key={period}>
                                                {periodKeyToLabel(period, monthlyCoverageHeatmap.resolvedGranularity)}
                                            </div>
                                        ))}
                                        <div className="lodge-heatmap-heading coverage-month-heading">Avg</div>
                                        {monthlyCoverageHeatmap.rows.map(row => (
                                            <React.Fragment key={row.lodge}>
                                                <div className="lodge-heatmap-lodge" title={row.lodge}>{row.lodge}</div>
                                                {monthlyCoverageHeatmap.periods.map(period => {
                                                    const cell = row.values[period];
                                                    const detail = {
                                                        lodge: row.lodge,
                                                        period,
                                                        periodLabel: periodKeyToLabel(period, monthlyCoverageHeatmap.resolvedGranularity),
                                                        cell
                                                    };
                                                    return (
                                                        <div
                                                            className={`coverage-cell ${getCoverageCellClassName(cell)}`}
                                                            key={`${row.lodge}-${period}`}
                                                            title={`${row.lodge} ${period}: ${formatPercent(cell?.percent)} complete (${cell?.agendas || 0} agenda, ${cell?.minutes || 0} minutes, ${cell?.totalDocuments || 0} matching documents)`}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => setSelectedCoverageCell(detail)}
                                                            onKeyDown={event => {
                                                                if (event.key === "Enter" || event.key === " ") {
                                                                    event.preventDefault();
                                                                    setSelectedCoverageCell(detail);
                                                                }
                                                            }}
                                                        >
                                                            <span>{formatPercent(cell?.percent)}</span>
                                                        </div>
                                                    );
                                                })}
                                                <div className="coverage-cell coverage-average">
                                                    <span>{formatPercent(row.averagePercent)}</span>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <div className="coverage-legend">
                                        <span><i className="coverage-missing" /> Missing</span>
                                        <span><i className="coverage-partial" /> Partial</span>
                                        <span><i className="coverage-complete" /> Complete</span>
                                    </div>
                                    {selectedCoverageCell && (
                                        <div className="coverage-detail-panel">
                                            <div>
                                                <h3>{selectedCoverageCell.lodge}</h3>
                                                <p>{selectedCoverageCell.periodLabel} · {formatPercent(selectedCoverageCell.cell?.percent)} complete</p>
                                            </div>
                                            <div className="coverage-detail-grid">
                                                {(selectedCoverageCell.cell?.details || []).map(detail => (
                                                    <div className="coverage-detail-item" key={`${selectedCoverageCell.lodge}-${detail.month}`}>
                                                        <strong>{detail.month}</strong>
                                                        <span>Expected: {detail.expectedEventTypes?.length ? detail.expectedEventTypes.join(", ") : "Meeting"}</span>
                                                        <span>Found: {detail.documentTypes?.length ? detail.documentTypes.join(", ") : "No agenda/minutes found"}</span>
                                                        <span>{detail.agendas || 0} agenda · {detail.minutes || 0} minutes</span>
                                                        {detail.scheduleNote && <span>{detail.scheduleNote}</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {!lodgeSummaryLoading && !lodgeSummaryError && lodgeSummaryView === "counts" && lodgeHeatmap.rows.length === 0 && (
                                <div className="analytics-empty">No lodge document data for this range.</div>
                            )}
                            {!lodgeSummaryLoading && !lodgeSummaryError && lodgeSummaryView === "counts" && lodgeHeatmap.rows.length > 0 && (
                                <div className="lodge-heatmap-scroll">
                                    <div
                                        className="lodge-heatmap"
                                        style={{ gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${lodgeHeatmap.columns.length}, minmax(96px, 1fr))` }}
                                    >
                                        <div className="lodge-heatmap-heading">Lodge</div>
                                        {lodgeHeatmap.columns.map(column => (
                                            <div className="lodge-heatmap-heading" key={column.key}>{column.label}</div>
                                        ))}
                                        {lodgeHeatmap.rows.map(row => (
                                            <React.Fragment key={row.lodge}>
                                                <div className="lodge-heatmap-lodge" title={row.lodge}>{row.lodge}</div>
                                                {lodgeHeatmap.columns.map(column => {
                                                    const value = row.values[column.key] || 0;
                                                    return (
                                                        <div
                                                            className="lodge-heatmap-cell"
                                                            key={`${row.lodge}-${column.key}`}
                                                            style={getHeatmapCellStyle(value, lodgeHeatmap.max)}
                                                            title={`${row.lodge}: ${formatCount(value)} ${column.label}`}
                                                        >
                                                            {formatCount(value)}
                                                        </div>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Daily trend */}
                    {showUsage && <section className="analytics-section-panel">
                        <div className="analytics-section-heading">
                            <div>
                                <h2>Usage Rhythm</h2>
                                <p>How members and administrators are moving through the site.</p>
                            </div>
                        </div>
                    <div className="chart-container line-chart">
                        <div className="chart-wrapper">
                            {dailyLine && <Line data={dailyLine} options={dailyLineOpts} />}
                        </div>
                    </div>

                    {/* Hour × weekday (stacked) */}
                    <div className="chart-container bar-chart">
                        <div className="chart-wrapper">
                            {hourByDowBar && <Bar data={hourByDowBar} options={hourByDowOpts} />}
                        </div>
                    </div>
                    </section>}

                    {showUsage && <section className="analytics-section-panel analytics-visitor-intelligence">
                        <div className="analytics-section-heading">
                            <div>
                                <h2>Visitor Intelligence</h2>
                                    <p>Anonymous site-wide visitors grouped by the best captured location signal and device type.</p>
                            </div>
                        </div>
                        <div className="analytics-visitor-grid">
                            <article className="analytics-map-card">
                                <div className="analytics-card-heading">
                                    <div>
                                        <h3>Site-Wide Visitor Map</h3>
                                        <p>OpenStreetMap background with heat clusters for mapped visitor locations.</p>
                                    </div>
                                    <span>{mappedResearchLocations.reduce((sum, row) => sum + row.visitors, 0).toLocaleString()} mapped visitors</span>
                                </div>
                                <div className="analytics-leaflet-map">
                                    <MapContainer
                                        center={mappedResearchLocations[0]?.point ? [mappedResearchLocations[0].point.lat, mappedResearchLocations[0].point.lng] : [-28.5, 24]}
                                        zoom={mappedResearchLocations.length === 1 ? 4 : 2}
                                        minZoom={2}
                                        scrollWheelZoom
                                        wheelDebounceTime={80}
                                        wheelPxPerZoomLevel={80}
                                        style={{ height: "100%", width: "100%" }}
                                    >
                                        <FitMapToPoints points={mappedResearchLocations} />
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        <VisitorHeatLayer points={mappedResearchLocations} />
                                    </MapContainer>
                                    {!mappedResearchLocations.length && (
                                        <div className="analytics-map-empty">
                                            No mappable country or IP lookup locations captured yet.
                                        </div>
                                    )}
                                </div>
                                <div className="analytics-location-list">
                                    {researchLocations.slice(0, 6).map((row, index) => (
                                        <div key={`${row.label}-${index}`}>
                                            <span>{row.label}</span>
                                            <strong>{row.visitors.toLocaleString()} visitor{row.visitors === 1 ? "" : "s"} · {row.sessions.toLocaleString()} session{row.sessions === 1 ? "" : "s"}</strong>
                                        </div>
                                    ))}
                                    {unknownResearchLocationSessions > 0 && (
                                        <small>{unknownResearchLocationSessions.toLocaleString()} session{unknownResearchLocationSessions === 1 ? "" : "s"} have no country or IP lookup location yet.</small>
                                    )}
                                </div>
                            </article>
                            <article className="analytics-map-card">
                                <div className="analytics-card-heading">
                                    <div>
                                        <h3>Anonymous Device Type</h3>
                                        <p>Derived from site-wide user-agent metadata.</p>
                                    </div>
                                    <span>{researchDeviceRows.reduce((sum, row) => sum + row.visitors, 0).toLocaleString()} visitors</span>
                                </div>
                                <div className="analytics-device-chart">
                                    {researchDevicesDoughnut ? (
                                        <Doughnut
                                            data={researchDevicesDoughnut}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { position: "bottom" },
                                                    title: { display: false },
                                                },
                                            }}
                                        />
                                    ) : (
                                        <div className="analytics-map-empty">No device data captured yet.</div>
                                    )}
                                </div>
                                <div className="analytics-location-list">
                                    {researchDeviceRows.map((row) => (
                                        <div key={row.device}>
                                            <span>{row.device}</span>
                                            <strong>{row.visitors.toLocaleString()} visitor{row.visitors === 1 ? "" : "s"} · {row.sessions.toLocaleString()} session{row.sessions === 1 ? "" : "s"}</strong>
                                        </div>
                                    ))}
                                </div>
                            </article>
                        </div>
                    </section>}

                    {/* Users & devices row */}
                    {showUsage && <div className="grid-2">
                        <div className="chart-container bar-chart stack">
                            <div className="chart-wrapper">
                                {recentUsersBar ? (
                                    <Bar data={recentUsersBar} options={recentUsersBarOpts} />
                                ) : (
                                    <div style={{ padding: 12, color: "#64748b" }}>
                                        No recent users data for this range.
                                    </div>
                                )}
                            </div>

                            {/* Recent users details */}
                            {extra?.recentUsers?.length ? (
                                <div className="mini-table">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Last Seen (SAST)</th>
                                            <th>Primary Device</th>
                                            <th>Events</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {extra.recentUsers.map(u => {
                                            const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
                                            return (
                                                <tr key={u.user_id}>
                                                    <td>{name || `User #${u.user_id}`}</td>
                                                    <td>{u.last_ts ? toSASTDateTime(u.last_ts) : "—"}</td>
                                                    <td>{u.primary_device || "—"}</td>
                                                    <td>{u.events}</td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>

                        <div className="chart-container">
                            <div className="chart-wrapper">
                                {devicesDoughnut ? (
                                    <Doughnut
                                        data={devicesDoughnut}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { position: "bottom" },
                                                title: { display: true, text: "Devices" },
                                            },
                                        }}
                                    />
                                ) : (
                                    <div style={{ padding: 12, color: "#64748b" }}>
                                        No device data for this range.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>}

                    {/* NEW: Downloads row */}
                    {showDownloads && <section className="analytics-section-panel">
                        <div className="analytics-section-heading">
                            <div>
                                <h2>Document Demand</h2>
                                <p>See which users and lodge collections are drawing the most downloads.</p>
                            </div>
                            <div className="analytics-section-actions">
                                <button
                                    className="btn"
                                    type="button"
                                    onClick={() => exportCsv(
                                        `downloads-by-user-${from}-${to}.csv`,
                                        dlByUser.map((row) => ({
                                            user: row.display || [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || row.user_id || "",
                                            downloads: row.downloads,
                                        }))
                                    )}
                                    disabled={!dlByUser?.length}
                                >
                                    Export Users
                                </button>
                                <button
                                    className="btn"
                                    type="button"
                                    onClick={() => exportCsv(
                                        `downloads-by-entity-${from}-${to}.csv`,
                                        dlByDoc.map((row) => ({
                                            document_id: row.document_id,
                                            lodge: row.lodge || "Unknown",
                                            downloads: row.downloads,
                                        }))
                                    )}
                                    disabled={!dlByDoc?.length}
                                >
                                    Export Entities
                                </button>
                            </div>
                        </div>
                    <div className="grid-2">
                        {/* Downloads by User */}
                        <div className="chart-container stack">
                            <div className="chart-wrapper">
                                {dlByUserBar ? (
                                    <Bar data={dlByUserBar} options={dlByUserBarOpts} />
                                ) : (
                                    <div style={{ padding: 12, color: "#64748b" }}>
                                        No download-by-user data for this range.
                                    </div>
                                )}
                            </div>

                            {dlByUser?.length ? (
                                <div className="mini-table">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>User</th>
                                            <th>Downloads</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {dlByUser.map(u => {
                                            const name =
                                                [u.first_name, u.last_name].filter(Boolean).join(" ").trim()
                                                || u.display
                                                || (u.user_id ? `User #${u.user_id}` : "—");
                                            return (
                                                <tr key={u.user_id ?? name}>
                                                    <td title={name}>{name}</td>
                                                    <td>{u.downloads}</td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>

                        {/* Downloads by Document */}
                        <div className="chart-container stack">
                            <div className="chart-wrapper">
                                {dlByDocBar ? (
                                    <Bar data={dlByDocBar} options={dlByDocBarOpts} />
                                ) : (
                                    <div style={{ padding: 12, color: "#64748b" }}>
                                        No download-by-document data for this range.
                                    </div>
                                )}
                            </div>

                            {dlByDoc?.length ? (
                                <div className="mini-table">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Entity (Lodge)</th>
                                            <th>Downloads</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {dlByDoc.map(d => (
                                            <tr key={d.document_id}>
                                                <td>{d.lodge || "Unknown"}</td>
                                                <td>{d.downloads}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    </section>}

                    {/* Top lists */}
                    {showUsage && <div className="top-lists">
                        <div className="card">
                            <h3>Top Paths</h3>
                            <ol>
                                {topPaths.map(p => (
                                    <li key={p.path}>
                                        <code>{p.path}</code>
                                        <span>{p.count}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="card">
                            <h3>Interaction Types</h3>
                            <ol>
                                {topTypes.map(t => (
                                    <li key={t.type}>
                                        <span>{t.type}</span>
                                        <span>{t.count}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="card">
                            <h3>Countries</h3>
                            <ol>
                                {topCountries.map(c => (
                                    <li key={c.country || "Unknown"}>
                                        <span>{c.country || "Unknown"}</span>
                                        <span>{c.count}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>}

                    {/* Recent Activity */}
                    {showActivity && <div className="recent analytics-section-panel">
                        <div className="analytics-section-heading">
                            <div>
                                <h2>Recent Activity</h2>
                                <p>Latest logged actions, useful for troubleshooting and light audit trails.</p>
                            </div>
                            <button
                                className="btn"
                                type="button"
                                onClick={() => exportCsv(
                                    `recent-activity-${from}-${to}.csv`,
                                    recent.map((row) => ({
                                        time_sast: toSASTDateTime(row.timestamp),
                                        user: (row.first_name || row.last_name) ? `${row.first_name || ""} ${row.last_name || ""}`.trim() : row.user_id || "",
                                        type: row.interaction_type,
                                        path: row.request_path,
                                        session: row.session_id,
                                        ip: row.ip,
                                    }))
                                )}
                                disabled={!recent?.length}
                            >
                                Export Activity
                            </button>
                        </div>
                        <table>
                            <thead>
                            <tr>
                                <th>Time (SAST)</th>
                                <th>User</th>
                                <th>Type</th>
                                <th>Path</th>
                                <th>Session</th>
                                <th>IP</th>
                            </tr>
                            </thead>
                            <tbody>
                            {recent.map(r => {
                                const name = (r.first_name || r.last_name)
                                    ? `${r.first_name || ""} ${r.last_name || ""}`.trim()
                                    : (r.user_id ? `User #${r.user_id}` : "—");
                                return (
                                    <tr key={r.id}>
                                        <td>{toSASTDateTime(r.timestamp)}</td>
                                        <td>{name}</td>
                                        <td>{r.interaction_type}</td>
                                        <td><code>{r.request_path}</code></td>
                                        <td>{r.session_id}</td>
                                        <td>{r.ip}</td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>}
                </>
            )}
        </div>
    );
}
